import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MindWiseService, { 
  type MindWiseSyncResponse, 
  type MindWiseSyncLog, 
  type MindWiseTestScenario 
} from "@/services/MindWiseService";
import {
  CheckCircle2,
  Brain,
  Activity,
  ShieldCheck,
  ShieldAlert,
  FileText,
  Clock,
  History,
  Layers,
  Sparkles,
  RefreshCw,
  Info,
  FlaskConical,
  AlertTriangle
} from "lucide-react";

interface MindWiseSyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  syncResult: MindWiseSyncResponse | null;
  patientId: string;
  patientName?: string;
  onReSync?: (scenario?: MindWiseTestScenario) => void;
  isSyncing?: boolean;
}

export const MindWiseSyncDialog: React.FC<MindWiseSyncDialogProps> = ({
  open,
  onOpenChange,
  syncResult,
  patientId,
  patientName = "Patient",
  onReSync,
  isSyncing = false,
}) => {
  const [activeTab, setActiveTab] = useState<"summary" | "history" | "payload">("summary");
  const [historyLogs, setHistoryLogs] = useState<MindWiseSyncLog[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState<MindWiseTestScenario>("happy_path");

  const results = syncResult?.sync_results;
  const phq9 = results?.phq9;
  const gad7 = results?.gad7;
  const note = results?.note;
  const isDuplicate = syncResult?.already_submitted;
  const isHighRisk = phq9?.data?.selfHarmRisk;

  useEffect(() => {
    if (open) {
      loadHistory();
    }
  }, [open, patientId, syncResult]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const logs = await MindWiseService.getSyncLogs(patientId);
      setHistoryLogs(logs);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleScenarioChange = (scenario: MindWiseTestScenario) => {
    setSelectedScenario(scenario);
    MindWiseService.setTestScenario(scenario);
    if (onReSync) {
      onReSync(scenario);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl sm:max-w-3xl w-[94vw] max-h-[86vh] flex flex-col p-0 overflow-hidden bg-white border border-gray-100 shadow-2xl rounded-2xl sm:rounded-3xl [&>[data-slot=dialog-close]]:text-white [&>[data-slot=dialog-close]]:hover:text-white [&>[data-slot=dialog-close]]:opacity-90 [&>[data-slot=dialog-close]]:hover:opacity-100 [&>[data-slot=dialog-close]]:bg-white/10 [&>[data-slot=dialog-close]]:hover:bg-white/25 [&>[data-slot=dialog-close]]:p-1.5 [&>[data-slot=dialog-close]]:rounded-lg transition-all">
        {/* Modal Header */}
        <div className={`p-4 sm:p-5 text-white relative shrink-0 transition-colors ${
          isHighRisk 
            ? "bg-gradient-to-r from-rose-700 via-red-700 to-amber-700" 
            : isDuplicate 
              ? "bg-gradient-to-r from-amber-700 via-orange-700 to-indigo-800"
              : "bg-gradient-to-r from-blue-700 via-indigo-700 to-emerald-700"
        }`}>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-blue-100 mb-0.5 pr-8">
            <Sparkles className="w-3.5 h-3.5 text-yellow-300" />
            MindWise EHR Synchronization
            <span className="ml-auto bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur-sm border border-white/20 flex items-center gap-1 font-bold">
              <FlaskConical className="w-3 h-3" />
              Mock Mode
            </span>
          </div>

          <DialogTitle className="text-xl sm:text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            {isHighRisk ? (
              <>
                Assessments Synced (Suicide Risk)
                <ShieldAlert className="w-5 h-5 text-yellow-300 inline animate-bounce" />
              </>
            ) : isDuplicate ? (
              <>
                Duplicate Assessment Handled (409)
                <AlertTriangle className="w-5 h-5 text-yellow-300 inline" />
              </>
            ) : (
              <>
                Assessment Sync Complete
                <CheckCircle2 className="w-5 h-5 text-emerald-300 inline" />
              </>
            )}
          </DialogTitle>

          <DialogDescription className="text-blue-100 text-xs sm:text-sm mt-0.5 line-clamp-1">
            {syncResult?.message || `Outreach note and assessment scores filed into MindWise EMR chart for ${patientName}.`}
          </DialogDescription>

          {/* Quick ID Chips */}
          <div className="flex flex-wrap items-center gap-2 mt-2.5 pt-2 border-t border-white/15 text-[11px]">
            <div className="bg-black/25 backdrop-blur-sm px-2.5 py-0.5 rounded-lg flex items-center gap-1.5 border border-white/10">
              <span className="text-blue-200">Session ID:</span>
              <code className="font-mono text-white font-semibold">
                {results?.sessionId || "sess_mw_7b8192f0"}
              </code>
            </div>
            <div className="bg-black/25 backdrop-blur-sm px-2.5 py-0.5 rounded-lg flex items-center gap-1.5 border border-white/10">
              <span className="text-blue-200">Patient ID:</span>
              <code className="font-mono text-emerald-200 font-semibold">
                {results?.patientId || "MW-PT-9042"}
              </code>
            </div>
            <div className="bg-black/25 backdrop-blur-sm px-2.5 py-0.5 rounded-lg flex items-center gap-1.5 border border-white/10">
              <span className="text-blue-200">Status:</span>
              <span className="font-semibold text-emerald-300">
                {isDuplicate ? "409 OK" : "201 Created"}
              </span>
            </div>
          </div>
        </div>

        {/* QA Test Scenario Quick Switcher */}
        <div className="bg-amber-50/80 border-b border-amber-200/80 px-4 py-2 flex flex-wrap items-center justify-between gap-1.5 text-xs shrink-0">
          <div className="flex items-center gap-1 font-bold text-amber-900 text-[11px]">
            <FlaskConical className="w-3.5 h-3.5 text-amber-700" />
            <span>Test Scenarios:</span>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={() => handleScenarioChange("happy_path")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                selectedScenario === "happy_path"
                  ? "bg-emerald-600 text-white shadow-xs"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
              title="TC-MW-02: Happy Path Sync"
            >
              ✓ Happy Path
            </button>

            <button
              onClick={() => handleScenarioChange("empty_state")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                selectedScenario === "empty_state"
                  ? "bg-amber-600 text-white shadow-xs"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
              title="TC-MW-03: Empty State (No Assessments)"
            >
              Empty (TC-03)
            </button>

            <button
              onClick={() => handleScenarioChange("duplicate_409")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                selectedScenario === "duplicate_409"
                  ? "bg-blue-600 text-white shadow-xs"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
              title="TC-MW-04: Duplicate 409 Conflict Handling"
            >
              409 Duplicate
            </button>

            <button
              onClick={() => handleScenarioChange("network_error")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                selectedScenario === "network_error"
                  ? "bg-red-600 text-white shadow-xs"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
              title="TC-MW-05: Server Error / Offline Resilience"
            >
              Network Error
            </button>

            <button
              onClick={() => handleScenarioChange("high_risk")}
              className={`px-2 py-0.5 rounded-md text-[11px] font-semibold transition-all ${
                selectedScenario === "high_risk"
                  ? "bg-rose-600 text-white shadow-xs"
                  : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
              }`}
              title="Suicide Risk: Item 9 positive flag"
            >
              ⚠️ Item 9
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1.5 px-4 pt-2 border-b border-gray-100 bg-gray-50/60 shrink-0">
          <button
            onClick={() => setActiveTab("summary")}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === "summary"
                ? "border-blue-600 text-blue-700 bg-white rounded-t-lg shadow-xs"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            Sync Overview
          </button>

          <button
            onClick={() => setActiveTab("history")}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === "history"
                ? "border-blue-600 text-blue-700 bg-white rounded-t-lg shadow-xs"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            <History className="w-3.5 h-3.5" />
            Audit Logs ({historyLogs.length})
          </button>

          <button
            onClick={() => setActiveTab("payload")}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-all flex items-center gap-1.5 ${
              activeTab === "payload"
                ? "border-blue-600 text-blue-700 bg-white rounded-t-lg shadow-xs"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            Raw JSON
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-4 sm:p-5 flex-1 min-h-0 overflow-y-auto space-y-3.5">
          {activeTab === "summary" && (
            <div className="space-y-3">
              {/* Step 1: Note */}
              <div className="p-3 rounded-xl border border-blue-100 bg-blue-50/50 flex flex-col gap-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-[11px] shadow-xs">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-xs">
                        Encounter Outreach Note Created
                      </h4>
                      <p className="text-[11px] text-gray-500">
                        Channel: {note?.outreachChannel || "SMS"} • Performed By: {note?.performedBy || "Member"}
                      </p>
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full border border-emerald-200">
                    ✓ Ingested
                  </span>
                </div>
                <p className="text-xs text-gray-700 bg-white p-2.5 rounded-lg border border-blue-100/80 italic">
                  "{note?.summary || "Completed behavioral health screening outreach for patient via SMS/Mobile. Encounter recorded in MindWise EMR chart."}"
                </p>
              </div>

              {/* Assessment Grid: PHQ-9 and GAD-7 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* PHQ-9 Card */}
                <div className={`p-3.5 rounded-xl border flex flex-col justify-between shadow-xs ${
                  isHighRisk 
                    ? "border-red-300 bg-red-50/50" 
                    : "border-amber-200 bg-gradient-to-br from-amber-50/60 to-white"
                }`}>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
                        isHighRisk 
                          ? "bg-red-200 text-red-900 border-red-300" 
                          : "bg-amber-100 text-amber-900 border-amber-200"
                      }`}>
                        <Brain className="w-3 h-3 text-amber-700" />
                        PHQ-9 Depression
                      </span>
                      <span className="text-[10px] font-mono text-gray-500">LOINC 44261-6</span>
                    </div>

                    <div className="mt-2 flex items-baseline justify-between">
                      <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                        {phq9?.data?.total ?? 12}{" "}
                        <span className="text-xs font-normal text-gray-500">/ 27</span>
                      </span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${
                        isHighRisk 
                          ? "bg-red-100 text-red-800 border-red-300" 
                          : "bg-amber-100 text-amber-800 border-amber-300"
                      }`}>
                        {phq9?.data?.severity || "Moderate Depression"}
                      </span>
                    </div>

                    {/* Item 9 Suicide Risk Trigger */}
                    <div className="mt-2 p-2 rounded-lg bg-white border border-amber-100 flex items-center justify-between text-[11px]">
                      <span className="text-gray-600 font-medium">Question 9 (Self-Harm):</span>
                      {isHighRisk ? (
                        <span className="inline-flex items-center gap-1 font-bold text-red-700 animate-pulse">
                          <ShieldAlert className="w-3.5 h-3.5 text-red-600" />
                          POSITIVE (Alert)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                          Negative (Score 0)
                        </span>
                      )}
                    </div>

                    {/* Answers pills */}
                    <div className="mt-2">
                      <span className="text-[10px] text-gray-500 block mb-1">Answers Sent (Q1–Q9):</span>
                      <div className="flex flex-wrap gap-1 font-mono text-[11px]">
                        {(phq9?.answers_sent || [2, 2, 1, 2, 1, 1, 2, 1, 0]).map((ans, idx) => (
                          <span
                            key={idx}
                            className={`w-5 h-5 rounded-md border flex items-center justify-center font-bold ${
                              idx === 8 && ans > 0
                                ? "bg-red-500 text-white border-red-600"
                                : "bg-gray-100 text-gray-700 border-gray-200"
                            }`}
                            title={`Q${idx + 1}: ${ans}`}
                          >
                            {ans}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-t border-amber-100/80 flex items-center justify-between text-[10px] text-gray-500">
                    <span>Clinical Review:</span>
                    <span className="font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                      MindWise "In Review"
                    </span>
                  </div>
                </div>

                {/* GAD-7 Card */}
                <div className="p-3.5 rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white flex flex-col justify-between shadow-xs">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-900 uppercase tracking-wider bg-indigo-100 px-2 py-0.5 rounded-md border border-indigo-200">
                        <Activity className="w-3 h-3 text-indigo-700" />
                        GAD-7 Anxiety
                      </span>
                      <span className="text-[10px] font-mono text-gray-500">LOINC 70274-6</span>
                    </div>

                    <div className="mt-2 flex items-baseline justify-between">
                      <span className="text-2xl font-extrabold text-gray-900 tracking-tight">
                        {gad7?.data?.total ?? 10}{" "}
                        <span className="text-xs font-normal text-gray-500">/ 21</span>
                      </span>
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">
                        {gad7?.data?.severity || "Moderate Anxiety"}
                      </span>
                    </div>

                    <div className="mt-2 p-2 rounded-lg bg-white border border-indigo-100 flex items-center justify-between text-[11px]">
                      <span className="text-gray-600 font-medium">Validation:</span>
                      <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        7 / 7 Complete
                      </span>
                    </div>

                    {/* Answers pills */}
                    <div className="mt-2">
                      <span className="text-[10px] text-gray-500 block mb-1">Answers Sent (Q1–Q7):</span>
                      <div className="flex flex-wrap gap-1 font-mono text-[11px]">
                        {(gad7?.answers_sent || [2, 1, 2, 1, 1, 1, 2]).map((ans, idx) => (
                          <span
                            key={idx}
                            className="w-5 h-5 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center font-bold text-gray-700"
                            title={`Q${idx + 1}: ${ans}`}
                          >
                            {ans}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-2 border-t border-indigo-100/80 flex items-center justify-between text-[10px] text-gray-500">
                    <span>Clinical Review:</span>
                    <span className="font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">
                      MindWise "In Review"
                    </span>
                  </div>
                </div>
              </div>

              {/* Guardrails summary */}
              <div className="p-2.5 bg-emerald-50 rounded-xl border border-emerald-200 flex items-start gap-2 text-[11px] text-emerald-900">
                <Info className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold">TC-MW-07 HIPAA Boundary:</span> Audio supplements isolated in private vault. Only numeric assessment scores transferred.
                </div>
              </div>
            </div>
          )}

          {activeTab === "history" && (
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-gray-800">MindWise Sync Audit Ledger (TC-MW-08)</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={loadHistory}
                  disabled={loadingHistory}
                  className="h-7 px-2 text-[11px] text-blue-600"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${loadingHistory ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              {historyLogs.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-xs">
                  No sync logs recorded yet.
                </div>
              ) : (
                <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-xs">
                  {historyLogs.map((log) => (
                    <div key={log.id} className="p-2.5 flex items-center justify-between hover:bg-gray-50/80 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`px-2.5 py-1 min-w-[58px] rounded-lg flex items-center justify-center font-bold text-[11px] whitespace-nowrap shrink-0 mr-1.5 shadow-xs ${
                            log.instrument === "PHQ-9"
                              ? "bg-amber-100 text-amber-900 border border-amber-300"
                              : "bg-indigo-100 text-indigo-900 border border-indigo-300"
                          }`}
                        >
                          {log.instrument}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-bold text-gray-900">
                              Score: {log.total_score} ({log.severity})
                            </span>
                            <span className="text-[9px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.2 rounded-full border border-emerald-200">
                              HTTP {log.mindwise_http_status}
                            </span>
                          </div>
                          <p className="text-[10px] text-gray-500 font-mono">
                            Session: {log.session_id} • Ref: {log.reference_id || "mh-sync"}
                          </p>
                        </div>
                      </div>

                      <div className="text-right text-[10px] text-gray-400">
                        <Clock className="w-2.5 h-2.5 inline mr-1" />
                        {new Date(log.synced_at).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "payload" && (
            <div className="space-y-1.5">
              <span className="text-[11px] font-semibold text-gray-500">
                Outbound & Inbound JSON Response Contract:
              </span>
              <pre className="p-3 bg-gray-900 text-emerald-400 font-mono text-[11px] rounded-xl overflow-x-auto max-h-[36vh] border border-gray-800">
                {JSON.stringify(syncResult, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>Frontend Mock Sandbox (Zero Database Calls)</span>
          </div>

          <div className="flex items-center gap-2">
            {onReSync && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReSync(selectedScenario)}
                disabled={isSyncing}
                className="h-8 rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50 text-xs px-3"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${isSyncing ? "animate-spin" : ""}`} />
                {isSyncing ? "Syncing..." : "Re-Sync Now"}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-xs px-4"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
